
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Container, Card, Button, Spinner, Alert, Badge } from 'react-bootstrap';

const PaymentPage = () => {
    const { appointmentId } = useParams();
    const navigate = useNavigate();
    const [appointment, setAppointment] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [razorpayLoaded, setRazorpayLoaded] = useState(false);

    useEffect(() => {
        // Load Razorpay Script
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = () => setRazorpayLoaded(true);
        script.onerror = () => setError('Failed to load payment gateway');
        document.body.appendChild(script);

        // Fetch Appointment Details
        const fetchDetails = async () => {
            try {
                const res = await api.get(`/public/appointments/${appointmentId}/payment-details`);
                if (res.data.status === 'Paid') {
                    setAppointment({ ...res.data.appointment, isPaid: true });
                } else {
                    setAppointment(res.data.appointment);
                }
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError(err.response?.data?.message || 'Failed to load appointment details');
                setLoading(false);
            }
        };
        fetchDetails();
    }, [appointmentId]);

    const handlePayment = async () => {
        if (!appointment || !razorpayLoaded || !appointment.razorpayOrderId) {
            Swal.fire('Error', 'Payment configuration invalid or loading...', 'error');
            return;
        }

        const options = {
            key: appointment.keyId,
            amount: appointment.amount * 100, // already sent as amount, but if sent as rupees, multiply. BE sends as amount (paise? no check server).
            // Server: amount = order.amount / 100. So appointment.amount is in Rupees.
            // RZP expects paise.
            currency: "INR",
            name: appointment.clinicName || "Medoro Health",
            description: `Consultation with ${appointment.doctorName}`,
            order_id: appointment.razorpayOrderId,
            handler: async function (response) {
                // Verify Payment
                try {
                    Swal.fire({ title: 'Verifying Payment...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
                    const verifyRes = await api.post('/public/payment/verification', {
                        razorpay_order_id: response.razorpay_order_id,
                        razorpay_payment_id: response.razorpay_payment_id,
                        razorpay_signature: response.razorpay_signature,
                        appointmentId: appointment._id
                    });

                    if (verifyRes.data.status === 'success') {
                        setAppointment({ ...appointment, isPaid: true });
                        Swal.fire('Success', 'Appointment Confirmed! You will receive an email shortly.', 'success');
                    } else {
                        Swal.fire('Error', 'Payment verification failed. Please contact clinic.', 'error');
                    }
                } catch (e) {
                    console.error(e);
                    Swal.fire('Error', 'Verification error. Please contact support.', 'error');
                }
            },
            prefill: {
                name: appointment.guestName || '',
                // email: appointment.guestEmail, // Not fetched explicitly but could be
                // contact: appointment.guestPhone
            },
            theme: {
                color: "#0d6efd"
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
    };

    if (loading) return (
        <Container className="d-flex justify-content-center align-items-center vh-100">
            <Spinner animation="border" variant="primary" />
        </Container>
    );

    if (error) return (
        <Container className="d-flex justify-content-center align-items-center vh-100">
            <Alert variant="danger" className="text-center shadow-sm p-4 rounded-3 border-0">
                <h4 className="alert-heading fw-bold mb-3">Oops!</h4>
                <p className="mb-0">{error}</p>
                <Button variant="outline-danger" className="mt-3" onClick={() => navigate('/')}>Home</Button>
            </Alert>
        </Container>
    );

    return (
        <div className="bg-light min-vh-100 py-5">
            <Container>
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-5">
                        <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                            <div className="bg-primary text-white p-4 text-center">
                                <h4 className="mb-0 fw-bold">Appointment Payment</h4>
                                <p className="mb-0 text-white-50 text-sm">Medoro Health</p>
                            </div>
                            <Card.Body className="p-4 p-md-5">
                                {appointment.isPaid ? (
                                    <div className="text-center py-5">
                                        <div className="mb-4 text-success display-1">
                                            <i className="bi bi-check-circle-fill"></i> {/* Assuming Bootstrap Icons or replace with text */}
                                            ✓
                                        </div>
                                        <h3 className="fw-bold text-success mb-3">Payment Successful!</h3>
                                        <p className="text-secondary mb-4">
                                            Your appointment with <strong>{appointment.doctorName}</strong> on <strong>{new Date(appointment.date).toLocaleDateString()}</strong> is confirmed.
                                        </p>
                                        <Button variant="outline-primary" onClick={() => window.close()}>Close Window</Button>
                                    </div>
                                ) : (
                                    <>
                                        <div className="d-flex align-items-center mb-4 pb-4 border-bottom">
                                            <div className="flex-grow-1">
                                                <h5 className="fw-bold mb-1">{appointment.doctorName}</h5>
                                                <div className="text-secondary text-sm">{appointment.specialization}</div>
                                            </div>
                                            <Badge bg="light" text="dark" className="border px-3 py-2 fw-medium">
                                                {appointment.timeSlot}
                                            </Badge>
                                        </div>

                                        <div className="mb-4">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary">Clinic</span>
                                                <span className="fw-medium text-end">{appointment.clinicName}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary">Date</span>
                                                <span className="fw-medium">{new Date(appointment.date).toLocaleDateString()}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-secondary">Patient</span>
                                                <span className="fw-medium">{appointment.guestName}</span>
                                            </div>
                                        </div>

                                        <div className="bg-light p-3 rounded-3 mb-4 d-flex justify-content-between align-items-center">
                                            <span className="fw-bold text-secondary">Total Amount</span>
                                            <span className="h4 mb-0 fw-bold text-primary">₹{appointment.amount}</span>
                                        </div>

                                        <div className="d-grid">
                                            <Button
                                                variant="primary"
                                                size="lg"
                                                onClick={handlePayment}
                                                disabled={!razorpayLoaded}
                                                className="fw-bold shadow-sm"
                                            >
                                                {razorpayLoaded ? `Pay Securely` : 'Loading Gateway...'}
                                            </Button>
                                        </div>
                                        <div className="text-center mt-3">
                                            <small className="text-muted">Secured by Razorpay</small>
                                        </div>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default PaymentPage;
